// Safety field (§10.1, §22): a per-room grid over MapDef.bounds + pad at TUNE.bot.safetyCell
// resolution. Each cell holds the signed distance (meters) to the nearest void boundary: positive
// on ground, negative over void. Two refinements over a plain groundAt mask (docs/decisions/WP4.md):
//  - static rails (capsule solids with tag NONE or SLINGSHOT) are walls a pushed bean cannot cross,
//    so distances are geodesic and a railed edge is not a kill zone (Pinball's rail gaps are);
//  - ground that is about to vanish (a CRACKING cake tile, a warned teacup rim wedge) counts as void.
// The distance transform is the two-pass chamfer, repeated until stable because walls block it.
import { TUNE, VOID } from '../constants';
import { MapId, TileState } from '../ids';
import { CAKE_GRID } from '../maps/cake';
import { hexTileAt } from '../maps/geometry';
import { MAPS } from '../maps/index';
import { SolidKind, SolidTag, createSolid, type MapDef, type Solid } from '../maps/mapTypes';
import { distToSegmentSq } from '../math';
import type { Vec2, World } from '../types';
import { BOT_TUNE } from './tuning';
import { datan2 } from '../sim/dmath';

const INF = 1e9;
/** Safety reported for ground no void can reach (fully walled in). */
const UNREACHABLE = 99;
const MAX_PASSES = 12;
/** Floats per launch pad: ax, ay, bx, by, r, sling. */
const PAD_FIELDS = 6;

/** Teacup Sudden Death rim chips (§6.2): the annulus r ∈ [inner, outer] in `count` wedges centered at k·360/count. */
const TEACUP_WEDGES = { inner: 4.5, outer: 7.5, count: 8 } as const;

/** True where ground still exists but is scheduled to vanish (counts as void for safety). */
export function pendingVoid(w: World, x: number, y: number): boolean {
  switch (w.cfg.mapId) {
    case MapId.CAKE: {
      const t = hexTileAt(CAKE_GRID, x, y);
      // A fresh crack is still floor for a while (every step cracks a tile); one about to drop is not.
      return t >= 0 && w.map.tileState[t] === TileState.CRACKING && w.map.tileTimer[t] <= BOT_TUNE.cake.crackVoidTicks;
    }
    case MapId.TEACUP: {
      const warn = w.map.wedgeWarn;
      if (warn === 0) return false;
      const r = Math.hypot(x, y);
      if (r < TEACUP_WEDGES.inner || r > TEACUP_WEDGES.outer) return false;
      const step = (Math.PI * 2) / TEACUP_WEDGES.count;
      let k = Math.round(datan2(y, x) / step) % TEACUP_WEDGES.count;
      if (k < 0) k += TEACUP_WEDGES.count;
      return ((warn >> k) & 1) === 1;
    }
    default:
      return false;
  }
}

/** True for a static solid that stops a sliding bean (a rail): see the header. */
export function isWallSolid(s: Solid): boolean {
  return s.kind === SolidKind.CAPSULE && (s.tag === SolidTag.NONE || s.tag === SolidTag.SLINGSHOT)
    && s.omega === 0 && s.vx === 0 && s.vy === 0 && s.top >= BOT_TUNE.wallMinTop;
}

export class SafetyField {
  readonly cell = TUNE.bot.safetyCell;
  x0 = 0;
  y0 = 0;
  nx = 0;
  ny = 0;
  /** Signed distance to the nearest void boundary, meters (> 0 ground, < 0 void). */
  d = new Float64Array(0);
  /** 1 where a wall (rail) blocks falls. */
  wall = new Uint8Array(0);
  /** 1 where the cell center is ground (and not pending void). */
  ground = new Uint8Array(0);
  /** 1 where a static solid (bumper, candle, hub, cup, pedestal, vending machine) blocks walking. */
  block = new Uint8Array(0);
  /** 1 where a static hazard solid (a bumper, a slingshot) would launch a body that touches it. */
  launch = new Uint8Array(0);
  /** Map the field was built for (−1: never built). */
  mapId = -1;
  /** Round tick of the last build. */
  builtTick = -INF;
  /** Bumped on every build. */
  version = 0;
  /** Largest positive safety on the field (for normalizing scores). */
  maxSafety = 0;
  /**
   * Static launch pads as capsules: ax, ay, bx, by, r, sling per pad (a circle has a = b). Bumpers
   * launch; slingshot rails (sling = 1) only kick (no longer a crown pop since hazards stopped
   * knocking the crown off, INT-A), so the motor and hops ignore them unless asked.
   */
  private pads = new Float64Array(PAD_FIELDS * 24);
  padCount = 0;
  private tmp = new Float64Array(0);
  private src = new Uint8Array(0);
  private pass = new Uint8Array(0);
  private readonly solids: Solid[] = [];
  private map: MapDef | null = null;

  /** Rebuilds the grid from `map` (defaults to the world's map) at the world's current tick. */
  build(w: World, map: MapDef = MAPS[w.cfg.mapId], withPending = true): void {
    const pad = TUNE.bot.safetyPad;
    const c = this.cell;
    const b = map.bounds;
    if (this.mapId !== map.id || this.nx === 0) {
      this.x0 = b.minX - pad;
      this.y0 = b.minY - pad;
      this.nx = Math.floor((b.maxX + pad - this.x0) / c) + 1;
      this.ny = Math.floor((b.maxY + pad - this.y0) / c) + 1;
      const n = this.nx * this.ny;
      this.d = new Float64Array(n);
      this.tmp = new Float64Array(n);
      this.wall = new Uint8Array(n);
      this.ground = new Uint8Array(n);
      this.block = new Uint8Array(n);
      this.launch = new Uint8Array(n);
      this.src = new Uint8Array(n);
      this.pass = new Uint8Array(n);
    }
    this.mapId = map.id;
    this.map = map;
    this.builtTick = w.tick;
    this.version++;
    const { nx, ny } = this;
    const n = nx * ny;

    // Masks.
    let solidCount = 0;
    while (this.solids.length < BOT_TUNE.maxSolids) this.solids.push(createSolid());
    solidCount = map.solids(w, this.solids);
    this.wall.fill(0);
    const wallReach = BOT_TUNE.wallPadCells * c;
    for (let s = 0; s < solidCount; s++) {
      const sol = this.solids[s];
      if (!isWallSolid(sol)) continue;
      const reach = sol.r + wallReach;
      const i0 = Math.max(0, Math.floor((Math.min(sol.ax, sol.bx) - reach - this.x0) / c));
      const i1 = Math.min(nx - 1, Math.ceil((Math.max(sol.ax, sol.bx) + reach - this.x0) / c));
      const j0 = Math.max(0, Math.floor((Math.min(sol.ay, sol.by) - reach - this.y0) / c));
      const j1 = Math.min(ny - 1, Math.ceil((Math.max(sol.ay, sol.by) + reach - this.y0) / c));
      for (let j = j0; j <= j1; j++) {
        for (let i = i0; i <= i1; i++) {
          const x = this.x0 + i * c;
          const y = this.y0 + j * c;
          if (distToSegmentSq(x, y, sol.ax, sol.ay, sol.bx, sol.by) <= reach * reach) this.wall[j * nx + i] = 1;
        }
      }
    }
    this.block.fill(0);
    this.launch.fill(0);
    this.padCount = 0;
    for (let s = 0; s < solidCount; s++) {
      const sol = this.solids[s];
      if (sol.omega !== 0 || sol.vx !== 0 || sol.vy !== 0 || sol.top < BOT_TUNE.blockMinTop) continue;
      // Raised ground you can jump onto (Podium pedestals) is a destination, not an obstacle.
      if (sol.kind === SolidKind.CIRCLE && map.groundAt(w, sol.ax, sol.ay) > 0) continue;
      // Bumpers and other launch pads: keep a whole body width clear of them.
      const reach = sol.r + (sol.tag !== SolidTag.NONE ? BOT_TUNE.hazardBlockPad : BOT_TUNE.blockPad);
      const bx = sol.kind === SolidKind.CAPSULE ? sol.bx : sol.ax;
      const by = sol.kind === SolidKind.CAPSULE ? sol.by : sol.ay;
      if ((sol.tag === SolidTag.BUMPER || sol.tag === SolidTag.SLINGSHOT) && (this.padCount + 1) * PAD_FIELDS <= this.pads.length) {
        const k = this.padCount++ * PAD_FIELDS;
        this.pads[k] = sol.ax;
        this.pads[k + 1] = sol.ay;
        this.pads[k + 2] = bx;
        this.pads[k + 3] = by;
        this.pads[k + 4] = sol.r;
        this.pads[k + 5] = sol.tag === SolidTag.SLINGSHOT ? 1 : 0;
      }
      const i0 = Math.max(0, Math.floor((Math.min(sol.ax, bx) - reach - this.x0) / c));
      const i1 = Math.min(nx - 1, Math.ceil((Math.max(sol.ax, bx) + reach - this.x0) / c));
      const j0 = Math.max(0, Math.floor((Math.min(sol.ay, by) - reach - this.y0) / c));
      const j1 = Math.min(ny - 1, Math.ceil((Math.max(sol.ay, by) + reach - this.y0) / c));
      for (let j = j0; j <= j1; j++) {
        for (let i = i0; i <= i1; i++) {
          if (distToSegmentSq(this.x0 + i * c, this.y0 + j * c, sol.ax, sol.ay, bx, by) <= reach * reach) {
            this.block[j * nx + i] = 1;
            if (sol.tag === SolidTag.BUMPER) this.launch[j * nx + i] = 1;
          }
        }
      }
    }
    for (let j = 0; j < ny; j++) {
      for (let i = 0; i < nx; i++) {
        const k = j * nx + i;
        const x = this.x0 + i * c;
        const y = this.y0 + j * c;
        const g = map.groundAt(w, x, y);
        this.ground[k] = g !== VOID && !(withPending && pendingVoid(w, x, y)) ? 1 : 0;
      }
    }

    // Ground: distance to the nearest void cell, walls blocking.
    for (let k = 0; k < n; k++) {
      const isVoid = this.ground[k] === 0 && this.wall[k] === 0;
      this.src[k] = isVoid ? 1 : 0;
      this.pass[k] = this.ground[k] === 1 && this.wall[k] === 0 ? 1 : 0;
    }
    this.chamfer(this.tmp);
    const half = c / 2;
    let maxSafety = 0;
    for (let k = 0; k < n; k++) {
      if (this.pass[k] === 1) {
        const v = this.tmp[k] >= INF ? UNREACHABLE : Math.max(c * 0.1, this.tmp[k] - half);
        this.d[k] = v;
        if (v < UNREACHABLE && v > maxSafety) maxSafety = v;
      }
    }
    this.maxSafety = maxSafety;

    // Void: minus the distance to the nearest ground (or wall) cell; walls do not block here.
    for (let k = 0; k < n; k++) {
      const solid = this.ground[k] === 1 || this.wall[k] === 1;
      this.src[k] = solid ? 1 : 0;
      this.pass[k] = solid ? 0 : 1;
    }
    this.chamfer(this.tmp);
    for (let k = 0; k < n; k++) {
      if (this.pass[k] === 1) this.d[k] = -Math.max(c * 0.1, Math.min(UNREACHABLE, this.tmp[k] - half));
    }

    // Walls take the best neighboring ground value so lookups beside a rail stay sensible.
    for (let j = 0; j < ny; j++) {
      for (let i = 0; i < nx; i++) {
        const k = j * nx + i;
        if (this.wall[k] === 0) continue;
        let best = -INF;
        for (let dj = -1; dj <= 1; dj++) {
          for (let di = -1; di <= 1; di++) {
            const ii = i + di;
            const jj = j + dj;
            if (ii < 0 || jj < 0 || ii >= nx || jj >= ny) continue;
            const kk = jj * nx + ii;
            if (this.wall[kk] === 0 && this.ground[kk] === 1 && this.d[kk] > best) best = this.d[kk];
          }
        }
        this.d[k] = best > -INF ? best : 0;
      }
    }
  }

  /**
   * Chamfer distance from `src` cells (0) through `pass` cells, written into `out` (INF where
   * unreached). Forward and backward passes repeat until nothing changes. A diagonal step is
   * allowed only if one of the two orthogonal cells it squeezes between is open, so a one-cell
   * diagonal wall does not leak.
   */
  private chamfer(out: Float64Array): void {
    const { nx, ny, cell } = this;
    const a = cell;
    const b = cell * Math.SQRT2;
    const src = this.src;
    const pass = this.pass;
    for (let k = 0; k < nx * ny; k++) out[k] = src[k] === 1 ? 0 : INF;
    const open = (k: number): boolean => src[k] === 1 || pass[k] === 1;
    for (let iter = 0; iter < MAX_PASSES; iter++) {
      let changed = false;
      for (let j = 0; j < ny; j++) {
        for (let i = 0; i < nx; i++) {
          const k = j * nx + i;
          if (pass[k] === 0) continue;
          let v = out[k];
          if (i > 0 && out[k - 1] + a < v) v = out[k - 1] + a;
          if (j > 0) {
            const up = k - nx;
            if (out[up] + a < v) v = out[up] + a;
            if (i > 0 && out[up - 1] + b < v && (open(k - 1) || open(up))) v = out[up - 1] + b;
            if (i < nx - 1 && out[up + 1] + b < v && (open(k + 1) || open(up))) v = out[up + 1] + b;
          }
          if (v < out[k]) {
            out[k] = v;
            changed = true;
          }
        }
      }
      for (let j = ny - 1; j >= 0; j--) {
        for (let i = nx - 1; i >= 0; i--) {
          const k = j * nx + i;
          if (pass[k] === 0) continue;
          let v = out[k];
          if (i < nx - 1 && out[k + 1] + a < v) v = out[k + 1] + a;
          if (j < ny - 1) {
            const dn = k + nx;
            if (out[dn] + a < v) v = out[dn] + a;
            if (i < nx - 1 && out[dn + 1] + b < v && (open(k + 1) || open(dn))) v = out[dn + 1] + b;
            if (i > 0 && out[dn - 1] + b < v && (open(k - 1) || open(dn))) v = out[dn - 1] + b;
          }
          if (v < out[k]) {
            out[k] = v;
            changed = true;
          }
        }
      }
      if (!changed) break;
    }
  }

  /** Bilinear lookup of the signed safety at (x, y), clamped to the grid. */
  safety(x: number, y: number): number {
    const { nx, ny, cell } = this;
    if (nx === 0) return UNREACHABLE;
    let fx = (x - this.x0) / cell;
    let fy = (y - this.y0) / cell;
    const maxX = nx - 1.000001;
    const maxY = ny - 1.000001;
    fx = fx < 0 ? 0 : fx > maxX ? maxX : fx;
    fy = fy < 0 ? 0 : fy > maxY ? maxY : fy;
    const i = Math.floor(fx);
    const j = Math.floor(fy);
    const tx = fx - i;
    const ty = fy - j;
    const k = j * nx + i;
    const d = this.d;
    const top = d[k] + (d[k + 1] - d[k]) * tx;
    const bot = d[k + nx] + (d[k + nx + 1] - d[k + nx]) * tx;
    return top + (bot - top) * ty;
  }

  /** Writes ∇safety at (x, y) (central differences) into `out`. */
  gradient(x: number, y: number, out: Vec2): Vec2 {
    const h = this.cell * 0.5;
    out.x = (this.safety(x + h, y) - this.safety(x - h, y)) / (2 * h);
    out.y = (this.safety(x, y + h) - this.safety(x, y - h)) / (2 * h);
    return out;
  }

  /** Nearest cell index of (x, y), or −1 outside the grid. */
  cellAt(x: number, y: number): number {
    const i = Math.round((x - this.x0) / this.cell);
    const j = Math.round((y - this.y0) / this.cell);
    if (i < 0 || j < 0 || i >= this.nx || j >= this.ny) return -1;
    return j * this.nx + i;
  }

  /** A static solid stands at (x, y) (bumper, candle, hub, cup wall, pedestal side). */
  isBlocked(x: number, y: number): boolean {
    const k = this.cellAt(x, y);
    return k >= 0 && this.block[k] === 1;
  }

  /**
   * Distance along (dx, dy) from (x, y) to the first launch pad (bumper), in `TUNE.bot.rayStep`
   * steps up to `max`; `max` if none (shoving someone into a bumper is a launch, §6.3).
   */
  launchRayDist(x: number, y: number, dx: number, dy: number, max: number): number {
    const l = Math.hypot(dx, dy);
    if (l === 0) return max;
    const step = TUNE.bot.rayStep;
    for (let s = step; s <= max + 1e-9; s += step) {
      const k = this.cellAt(x + (dx / l) * s, y + (dy / l) * s);
      if (k < 0) return max;
      if (this.wall[k] === 1) return max;
      if (this.launch[k] === 1) return s;
    }
    return max;
  }

  /** A launch pad (bumper) would catch a body standing at (x, y). */
  isLaunch(x: number, y: number): boolean {
    const k = this.cellAt(x, y);
    return k >= 0 && this.launch[k] === 1;
  }

  /** Clearance (meters, can be negative) from (x, y) to the nearest launch pad's surface (slingshot rails too with `slings`). */
  padClearance(x: number, y: number, slings = false): number {
    let best = INF;
    const p = this.pads;
    for (let i = 0; i < this.padCount; i++) {
      const k = i * PAD_FIELDS;
      if (p[k + 5] === 1 && !slings) continue;
      const d = Math.sqrt(distToSegmentSq(x, y, p[k], p[k + 1], p[k + 2], p[k + 3])) - p[k + 4];
      if (d < best) best = d;
    }
    return best;
  }

  /**
   * A body of radius `r` moving straight from (x0, y0) to (x1, y1) touches a launch pad (bumper)
   * on the way, with `pad` meters to spare: a lunge, a hop or a dash along it ends in a launch.
   * Starting inside that clearance only counts if the path goes deeper (stepping away is fine).
   */
  launchOnSegment(x0: number, y0: number, x1: number, y1: number, r = 0.45, pad = 0.15, slings = false): boolean {
    if (this.padCount === 0) return false;
    const need = r + pad;
    const start = this.padClearance(x0, y0, slings);
    const len = Math.hypot(x1 - x0, y1 - y0);
    const n = Math.max(1, Math.ceil(len / (this.cell * 0.5)));
    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const c = this.padClearance(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, slings);
      if (c < need && (start >= need || c < start - 0.05)) return true;
    }
    return false;
  }

  isWall(x: number, y: number): boolean {
    const k = this.cellAt(x, y);
    return k >= 0 && this.wall[k] === 1;
  }

  cellX(k: number): number {
    return this.x0 + (k % this.nx) * this.cell;
  }

  cellY(k: number): number {
    return this.y0 + Math.floor(k / this.nx) * this.cell;
  }

  /**
   * `voidRayDist` (§22): marches `TUNE.bot.rayStep` steps from (x, y) along (dx, dy) through
   * `groundAt` and returns the distance to the first VOID sample, or `max` if none (a wall ends
   * the ray: nothing falls through a rail).
   */
  voidRayDist(w: World, x: number, y: number, dx: number, dy: number, max: number = TUNE.bot.rayMax): number {
    const l = Math.hypot(dx, dy);
    if (l === 0) return max;
    const ux = dx / l;
    const uy = dy / l;
    const map = this.map ?? MAPS[w.cfg.mapId];
    const step = TUNE.bot.rayStep;
    for (let s = step; s <= max + 1e-9; s += step) {
      const px = x + ux * s;
      const py = y + uy * s;
      if (this.isWall(px, py)) return max;
      if (map.groundAt(w, px, py) === VOID) return s;
    }
    return max;
  }
}
